import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Forbidden } from '@/modules/auth/Forbidden';
import { useAuthorization } from '@/modules/auth/useAuthorization';
import { apiErrorMessage } from '@/shared/api-client';
import { Button } from '@/shared/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/components/dialog';
import { Input } from '@/shared/ui/components/input';
import { Label } from '@/shared/ui/components/label';
import { venueSlotGranularityMinutes } from '@/modules/venue/slotGranularity';
import { useChangeVenueLocale, useUpdateVenueName, useVenues, type Venue } from './venues';

export function VenuesPage() {
  const { t } = useTranslation('common');
  const auth = useAuthorization();
  const canManage = auth.can('venue.manage');
  const canView = auth.can('venue.view') || canManage;
  const canEditLocale = auth.can('venue.edit_locale');
  const canManageWaiverTemplates = auth.can('venue.waiver_template.manage');

  const { data, isLoading, isError } = useVenues();

  const [viewing, setViewing] = useState<Venue | null>(null);
  const [editing, setEditing] = useState<Venue | null>(null);
  const [changingLocale, setChangingLocale] = useState<Venue | null>(null);

  if (!canView) return <Forbidden />;

  return (
    <section className="grid gap-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t('venues.title')}</h1>
        {canManage && (
          <Button asChild>
            <Link to="/admin/venues/new">{t('venues.actions.create')}</Link>
          </Button>
        )}
      </div>

      <WaiverManagementSection canManageWaiverTemplates={canManageWaiverTemplates} />

      {isLoading && <p className="text-muted-foreground">{t('venues.loading')}</p>}
      {isError && <p className="text-destructive">{t('venues.errors.loadFailed')}</p>}

      {data && (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/50 text-left">
              <tr>
                <th className="px-4 py-2 font-medium">{t('venues.columns.name')}</th>
                <th className="px-4 py-2 font-medium">{t('venues.columns.country')}</th>
                <th className="px-4 py-2 font-medium">{t('venues.columns.locale')}</th>
                <th className="px-4 py-2 font-medium">{t('venues.columns.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {data.items.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">
                    {t('venues.empty')}
                  </td>
                </tr>
              )}
              {data.items.map((venue) => (
                <tr key={venue.id} className="border-b last:border-0">
                  <td className="px-4 py-2 font-medium">{venue.name}</td>
                  <td className="px-4 py-2">{venue.countryCode}</td>
                  <td className="px-4 py-2 text-muted-foreground">
                    {venue.language} · {venue.currency} · {venue.timezone} ·{' '}
                    {venueSlotGranularityMinutes(venue.slotGranularityMinutes)}m
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => setViewing(venue)}>
                        {t('venues.actions.view')}
                      </Button>
                      {canManage && (
                        <Button size="sm" variant="outline" onClick={() => setEditing(venue)}>
                          {t('venues.actions.edit')}
                        </Button>
                      )}
                      {canEditLocale && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setChangingLocale(venue)}
                        >
                          {t('venues.actions.changeLocale')}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <VenueDetailDialog venue={viewing} onOpenChange={(open) => !open && setViewing(null)} />
      <EditNameDialog
        venue={editing}
        onOpenChange={(open) => !open && setEditing(null)}
        onSaved={() => toast.success(t('venues.toast.updated'))}
      />
      <ChangeLocaleDialog
        venue={changingLocale}
        onOpenChange={(open) => !open && setChangingLocale(null)}
        onSaved={() => toast.success(t('venues.toast.localeChanged'))}
      />
    </section>
  );
}

function WaiverManagementSection({
  canManageWaiverTemplates,
}: {
  canManageWaiverTemplates: boolean;
}) {
  const { t } = useTranslation('common');

  return (
    <section className="grid gap-4 rounded-xl border bg-card p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">{t('venues.waivers.title')}</h2>
          <p className="text-sm text-muted-foreground">{t('venues.waivers.description')}</p>
        </div>
        {canManageWaiverTemplates && (
          <Button variant="outline" asChild>
            <Link to="/admin/waivers">{t('venues.waivers.actions.create')}</Link>
          </Button>
        )}
      </div>
    </section>
  );
}

function VenueDetailDialog({
  venue,
  onOpenChange,
}: {
  venue: Venue | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation('common');
  if (!venue) return null;

  return (
    <Dialog open={venue !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{venue.name}</DialogTitle>
          <DialogDescription>{t('venues.detail.description')}</DialogDescription>
        </DialogHeader>
        <dl className="grid grid-cols-2 gap-2 text-sm">
          <dt className="text-muted-foreground">{t('venues.fields.country')}</dt>
          <dd>{venue.countryCode}</dd>
          <dt className="text-muted-foreground">{t('venues.fields.language')}</dt>
          <dd>{venue.language}</dd>
          <dt className="text-muted-foreground">{t('venues.fields.currency')}</dt>
          <dd>{venue.currency}</dd>
          <dt className="text-muted-foreground">{t('venues.fields.timezone')}</dt>
          <dd>{venue.timezone}</dd>
          <dt className="text-muted-foreground">{t('venues.fields.slotGranularity')}</dt>
          <dd>{venueSlotGranularityMinutes(venue.slotGranularityMinutes)} min</dd>
        </dl>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('actions.cancel')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditNameDialog({
  venue,
  onOpenChange,
  onSaved,
}: {
  venue: Venue | null;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation('common');
  const updateName = useUpdateVenueName();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (venue) {
      setName(venue.name);
      setError(null);
    }
  }, [venue]);

  if (!venue) return null;
  const currentVenue = venue;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      await updateName.mutateAsync({ id: currentVenue.id, name });
      onOpenChange(false);
      onSaved();
    } catch (caught) {
      setError(apiErrorMessage(t, 'venues', caught));
    }
  }

  return (
    <Dialog open={venue !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{t('venues.edit.title')}</DialogTitle>
            <DialogDescription>{t('venues.edit.description')}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            <Label htmlFor="loc-edit-name">{t('venues.fields.name')}</Label>
            <Input
              id="loc-edit-name"
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('actions.cancel')}
            </Button>
            <Button type="submit" disabled={updateName.isPending}>
              {updateName.isPending ? t('venues.edit.submitting') : t('venues.edit.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ChangeLocaleDialog({
  venue,
  onOpenChange,
  onSaved,
}: {
  venue: Venue | null;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation('common');
  const changeLocale = useChangeVenueLocale();
  const [language, setLanguage] = useState('');
  const [currency, setCurrency] = useState('');
  const [timezone, setTimezone] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (venue) {
      setLanguage(venue.language);
      setCurrency(venue.currency);
      setTimezone(venue.timezone);
      setConfirmed(false);
      setError(null);
    }
  }, [venue]);

  if (!venue) return null;
  const currentVenue = venue;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      await changeLocale.mutateAsync({
        id: currentVenue.id,
        input: { language, currency, timezone },
      });
      onOpenChange(false);
      onSaved();
    } catch (caught) {
      setError(apiErrorMessage(t, 'venues', caught));
    }
  }

  return (
    <Dialog open={venue !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{t('venues.locale.title', { name: venue.name })}</DialogTitle>
            <DialogDescription>{t('venues.locale.warning')}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            <Label htmlFor="loc-locale-language">{t('venues.fields.language')}</Label>
            <Input
              id="loc-locale-language"
              required
              value={language}
              onChange={(event) => setLanguage(event.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="loc-locale-currency">{t('venues.fields.currency')}</Label>
            <Input
              id="loc-locale-currency"
              required
              value={currency}
              onChange={(event) => setCurrency(event.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="loc-locale-timezone">{t('venues.fields.timezone')}</Label>
            <Input
              id="loc-locale-timezone"
              required
              value={timezone}
              onChange={(event) => setTimezone(event.target.value)}
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(event) => setConfirmed(event.target.checked)}
            />
            {t('venues.locale.confirm')}
          </label>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('actions.cancel')}
            </Button>
            <Button type="submit" disabled={changeLocale.isPending || !confirmed}>
              {changeLocale.isPending ? t('venues.locale.submitting') : t('venues.locale.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
